import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Wizard, WizardHeaderWithOverview, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { getFullUrl } from 'hubspot-url-utils';
import { getIsLocalDevelopment } from 'marketplace-ui-common/utils/urls';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getMarketplaceInstallSuccessUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { usePollForInstallStatus } from '../../hooks/usePollForInstallStatus';
import { HubSpotOAuthStep } from './HubSpotOAuthStep';
import { InstallSuccessStep } from './InstallSuccessStep';
import { ThirdPartyAuthStep } from './ThirdPartyAuthStep';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NoFooter = () => null;
const THIRD_PARTY_AUTH_TIMEOUT_MS = 3 * 60 * 1000;
export function InstallWizard({
  appContext,
  appId,
  features,
  appSignInConfiguration,
  isPreview,
  isUIE,
  iconUrl,
  installUrl,
  listingId,
  name,
  oauthQueryParams,
  onClose,
  portalId
}) {
  const skipThirdPartyAuth = appSignInConfiguration === null || appSignInConfiguration === void 0 ? void 0 : appSignInConfiguration.includes('OMIT_OAUTH');
  const {
    trackInteraction,
    isEmbedded
  } = useDetailsPageContext();
  const [stepIndex, setStepIndex] = useState(0);
  const [popUpWindow, setPopUpWindow] = useState(null);
  const [thirdPartyState, setThirdPartyState] = useState('');
  const [codeChallenge, setCodeChallenge] = useState('');
  const [codeChallengeMethod, setCodeChallengeMethod] = useState('');
  const [optionalScopes, setOptionalScopes] = useState('');
  const [specifiedScopes, setSpecifiedScopes] = useState('');
  const [hiddenIframe, setHiddenIframe] = useState(null);
  const [shouldPollFromThirdPartyStep, setShouldPollFromThirdPartyStep] = useState(false);
  const [skipInstallSuccessPoll, setSkipInstallSuccessPoll] = useState(false);

  // The third party step is always first (index 0) unless skipped
  const thirdPartyStepIndex = skipThirdPartyAuth ? -1 : 0;
  const isOnThirdPartyStep = !skipThirdPartyAuth && stepIndex === thirdPartyStepIndex;
  const completeStepIndex = skipThirdPartyAuth ? 1 : 2;

  // Ref to ensure the poll-result tracking fires only once
  const hasTrackedThirdPartyPollResult = useRef(false);
  const isLocalDevelopment = getIsLocalDevelopment();

  // Poll for install status only after popup closes or timeout while on the
  // ThirdPartyAuthStep. Passing undefined listingId disables the hook until
  // shouldPollFromThirdPartyStep becomes true.
  const {
    installed: thirdPartyPollInstalled,
    error: thirdPartyPollError
  } = usePollForInstallStatus({
    listingId: shouldPollFromThirdPartyStep ? listingId : undefined,
    published: isPreview !== true,
    appContext: appContext !== null && appContext !== void 0 ? appContext : 'app'
  });

  // Detect when the popup window is closed while on the ThirdPartyAuthStep
  useEffect(() => {
    if (!isOnThirdPartyStep || !popUpWindow || shouldPollFromThirdPartyStep) {
      return;
    }
    const interval = setInterval(() => {
      if (popUpWindow.closed) {
        clearInterval(interval);
        trackInteraction({
          action: 'rogue install popup closed polling start'
        });
        setShouldPollFromThirdPartyStep(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isOnThirdPartyStep, popUpWindow, shouldPollFromThirdPartyStep, trackInteraction]);

  // Start polling after a long timeout if the user is still on ThirdPartyAuthStep
  useEffect(() => {
    if (!isOnThirdPartyStep || shouldPollFromThirdPartyStep) return;
    const timeout = setTimeout(() => {
      trackInteraction({
        action: 'rogue install timeout polling start'
      });
      setShouldPollFromThirdPartyStep(true);
    }, THIRD_PARTY_AUTH_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isOnThirdPartyStep, shouldPollFromThirdPartyStep, trackInteraction]);

  // React to poll results from ThirdPartyAuthStep polling
  useEffect(() => {
    if (!shouldPollFromThirdPartyStep || hasTrackedThirdPartyPollResult.current) {
      return;
    }
    if (thirdPartyPollInstalled === true) {
      hasTrackedThirdPartyPollResult.current = true;
      trackInteraction({
        action: 'rogue install success'
      });
      setSkipInstallSuccessPoll(true);
      setStepIndex(completeStepIndex);
    } else if (thirdPartyPollError) {
      hasTrackedThirdPartyPollResult.current = true;
      trackInteraction({
        action: 'rogue install failed'
      });
    }
  }, [shouldPollFromThirdPartyStep, thirdPartyPollInstalled, thirdPartyPollError, trackInteraction, completeStepIndex]);
  useEffect(() => {
    const handleMessages = event => {
      // check that message originated from the same origin
      if (event.origin !== getFullUrl(isLocalDevelopment ? 'local' : 'app')) return;
      if (event.data && event.data.type === 'INSTALL_SUCCESS') {
        if (popUpWindow) popUpWindow.close();
        if (hiddenIframe) {
          hiddenIframe.remove();
          setHiddenIframe(null);
        }
        // Complete step index depends on whether we skipped third party auth
        setStepIndex(completeStepIndex);
      }
      if (event.data && event.data.type === 'THIRD_PARTY_AUTH_STATE') {
        if (popUpWindow) popUpWindow.close();
        setThirdPartyState(event.data.state || '');
        setCodeChallenge(event.data.codeChallenge || '');
        setCodeChallengeMethod(event.data.codeChallengeMethod || '');
        setOptionalScopes(event.data.optionalScopes || '');
        setSpecifiedScopes(event.data.scopes || ''); // scopes and conditional scopes specified by the app provider
        setStepIndex(1); // Move to HubSpot OAuth step
        trackInteraction({
          action: 'complete third party auth state'
        });
      }
    };

    // Fallback: when window.opener is null in the popup, ThirdPartyAuthState/InstallSuccess
    // write to localStorage instead. The storage event fires on this window even across browsing
    // context group boundaries where postMessage cannot reach.
    const handleStorageMessage = event => {
      if (!event.newValue || event.storageArea !== localStorage) return;
      if (event.key === 'THIRD_PARTY_AUTH_STATE') {
        let data;
        try {
          data = JSON.parse(event.newValue);
        } catch (_e) {
          return;
        }
        try {
          localStorage.removeItem('THIRD_PARTY_AUTH_STATE');
        } catch (_e) {
          // localStorage may be unavailable in some environments
        }
        if (popUpWindow) popUpWindow.close();
        setThirdPartyState(data.state || '');
        setCodeChallenge(data.codeChallenge || '');
        setCodeChallengeMethod(data.codeChallengeMethod || '');
        setOptionalScopes(data.optionalScopes || '');
        setSpecifiedScopes(data.scopes || '');
        setStepIndex(1);
        trackInteraction({
          action: 'complete third party auth state'
        });
      }
      if (event.key === 'INSTALL_SUCCESS') {
        try {
          localStorage.removeItem('INSTALL_SUCCESS');
        } catch (_e) {
          // localStorage may be unavailable in some environments
        }
        if (popUpWindow) popUpWindow.close();
        if (hiddenIframe) {
          hiddenIframe.remove();
          setHiddenIframe(null);
        }
        setStepIndex(completeStepIndex);
      }
    };
    const shouldListenForMessages = stepIndex === 0 && popUpWindow || stepIndex === 1 && (popUpWindow || hiddenIframe);
    if (shouldListenForMessages) {
      window.addEventListener('message', handleMessages);
      window.addEventListener('storage', handleStorageMessage);
    }
    const shouldRemoveListener = stepIndex === 2;
    if (shouldRemoveListener) {
      window.removeEventListener('message', handleMessages);
      window.removeEventListener('storage', handleStorageMessage);
    }
    return () => {
      window.removeEventListener('message', handleMessages);
      window.removeEventListener('storage', handleStorageMessage);
    };
  }, [trackInteraction, popUpWindow, stepIndex, isLocalDevelopment, hiddenIframe, skipThirdPartyAuth, completeStepIndex]);

  // Cleanup iframe on unmount
  useEffect(() => {
    return () => {
      if (hiddenIframe) {
        hiddenIframe.remove();
      }
    };
  }, [hiddenIframe]);
  const renderedHeaderIcons = useMemo(() => /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    justifySelf: "center",
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(Image, {
      src: iconUrl,
      blockSize: "64px",
      objectFit: "contain",
      responsive: false
    }), /*#__PURE__*/_jsx(IconHolder, {
      icon: EllipsesIcon,
      size: "1000",
      paddingBlock: "0",
      color: "icon-secondary-default"
    }), /*#__PURE__*/_jsx(IconHolder, {
      icon: SprocketIcon,
      size: "1000",
      padding: "0",
      color: "icon-secondary-default"
    })]
  }), [iconUrl]);
  const handlePopUpClick = useCallback(url => {
    if (popUpWindow && !popUpWindow.closed) {
      popUpWindow.focus();
      trackInteraction({
        action: 'focus third party sign in pop up'
      });
    } else {
      const windowObjReference = window.open(url, '_target', 'popup=true,width=600,height=800,top=200,left=200');
      setPopUpWindow(windowObjReference);
      trackInteraction({
        action: 'open third party sign in pop up'
      });
    }
  }, [trackInteraction, popUpWindow, setPopUpWindow]);
  const handleOAuthSuccess = useCallback(oauthRedirectUri => {
    setStepIndex(completeStepIndex);
    const url = new URL(oauthRedirectUri);
    url.searchParams.append('step', 'finalize');
    url.searchParams.append('state', skipThirdPartyAuth ? '' : thirdPartyState);
    url.searchParams.append('returnUrl', getMarketplaceInstallSuccessUrl(portalId !== null && portalId !== void 0 ? portalId : 0));
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.src = url.toString();
    document.body.appendChild(iframe);
    setHiddenIframe(iframe);
    trackInteraction({
      action: skipThirdPartyAuth ? 'initiate install final step (skipped third party auth)' : 'initiate secure install final step'
    });
  }, [skipThirdPartyAuth, thirdPartyState, trackInteraction, portalId, completeStepIndex]);
  const steps = useMemo(() => {
    const hubSpotStep = {
      name: unescapedText('detailsPage.install.hubSpotStep.name'),
      element: /*#__PURE__*/_jsx(HubSpotOAuthStep, {
        oauthQueryParams: oauthQueryParams,
        portalId: portalId,
        onOAuthSuccess: handleOAuthSuccess,
        codeChallenge: codeChallenge,
        codeChallengeMethod: codeChallengeMethod,
        optionalScopes: optionalScopes,
        specifiedScopes: specifiedScopes
      })
    };
    const thirdPartyStep = {
      name: unescapedText('detailsPage.install.thirdPartyStep.name', {
        name
      }),
      element: /*#__PURE__*/_jsx(ThirdPartyAuthStep, {
        isPreview: isPreview,
        name: name,
        portalId: portalId,
        renderedHeaderIcons: renderedHeaderIcons,
        popUpWindow: popUpWindow,
        onPopUpClick: handlePopUpClick,
        installUrl: installUrl
      })
    };
    const completeStep = {
      name: unescapedText('detailsPage.install.completeStep.name'),
      element: /*#__PURE__*/_jsx(InstallSuccessStep, {
        appId: appId,
        features: features,
        isEmbedded: isEmbedded,
        isPreview: isPreview,
        isUIE: isUIE,
        name: name,
        portalId: portalId,
        renderedHeaderIcons: renderedHeaderIcons,
        onClose: onClose,
        listingId: listingId,
        skipPoll: skipInstallSuccessPoll
      })
    };
    if (skipThirdPartyAuth) {
      // When skipping third party auth, only show HubSpot OAuth and complete steps
      return [hubSpotStep, completeStep];
    }
    return [thirdPartyStep, hubSpotStep, completeStep];
  }, [skipThirdPartyAuth, features, isPreview, name, oauthQueryParams, portalId, codeChallenge, codeChallengeMethod, optionalScopes, specifiedScopes, renderedHeaderIcons, handleOAuthSuccess, listingId, popUpWindow, handlePopUpClick, appId, isUIE, isEmbedded, onClose, installUrl, skipInstallSuccessPoll]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "conversational",
    width: 800,
    size: "auto",
    centered: true,
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(View, {
      blockSize: "70vh",
      marginBlockStart: "800",
      maxBlockSize: "600px",
      children: /*#__PURE__*/_jsx(Wizard, {
        blockSize: "95%",
        headerComponent: WizardHeaderWithOverview,
        stepIndex: stepIndex,
        onStepIndexChange: e => {
          const index = +e.target.value;
          setStepIndex(index);
        },
        onReject: () => null,
        onConfirm: () => null,
        footerComponent: NoFooter,
        children: steps.map((step, index) => /*#__PURE__*/_jsx(WizardStep, {
          paddingBlockEnd: "0",
          name: step.name,
          children: step.element
        }, index))
      })
    })]
  });
}